import BaseJoi from 'joi-browser';
import regex from './validateRegex';
import * as _ from 'lodash';
import { COPY_FIELD_ADDRESS_TARGET } from './constants';

/**
 * Joiの判定関数の実装方法確認のために電話番号の判定を行っている。
 * この関数を使えば
 *  tel: Joi.string().regex(regex.pattern.tel).required(),
 * 上記の代わりに下記のように記述可能
 *  tel: Joi.string().tel().required(),
 * @param {*} joi
 * 参考：https://github.com/hapijs/joi/issues/1538
 * faxもregex.pattern.telと違いがない為、省略
 */
export function tel(joi) {
  return {
    name: 'string',
    base: joi.string(),
    language: {
      tel: 'must be a valid format, (e.g.): 999-9999-9999 etc',
    },
    rules: [
      {
        name: 'tel',
        validate(params, value, state, options) {
          if (value.match(regex.pattern.tel) ? true : false) {
            return value;
          } else {
            return this.createError('string.tel', { value }, state, options);
          }
        },
      },
    ],
  };
}

const Joi = BaseJoi.extend(tel);

/**
 * @description 現場編集画面で使う バリデーションオブジェクト
 */
const fieldSchemeKeys = {
  name: Joi.string()
    .trim()
    .required()
    .max(60), // 現場名称

  postal_code: Joi.string()
    .regex(regex.pattern.zip)
    .allow(''), // 現場の郵便番号
  ordering_type_id: Joi.number().allow(null), //発注区分
  construction_type_id: Joi.number().allow(null), //工事種類
  address_prefecture: Joi.string().required(), // 都道府県
  address_city: Joi.string()
    .regex(regex.pattern.otherThanZenkakuMarkAndNumeric, {
      name: 'otherThanZenkakuMarkAndNumeric',
    })
    .max(255)
    .required(), // 現場の市区町村

  address_city_kana: Joi.string()
    .regex(regex.pattern.alphanumericAndKatakana, {
      name: 'alphanumericAndKatakana',
    })
    .max(255)
    .required(), // 現場の市区町村かな

  address_add_1: Joi.string()
    .regex(regex.pattern.otherThanZenkakuMarkAndNumeric, {
      name: 'otherThanZenkakuMarkAndNumeric',
    })
    .max(255)
    .required(), // 現場の住所その１

  address_add_1_kana: Joi.string()
    .regex(regex.pattern.alphanumericAndKatakana, {
      name: 'alphanumericAndKatakana',
    })
    .max(255)
    .required(), // 現場の住所その１

  address_add_2: Joi.string()
    .regex(regex.pattern.otherThanZenkakuMarkAndNumeric, {
      name: 'otherThanZenkakuMarkAndNumeric',
    })
    .max(255)
    .allow(null, ''), // 現場の住所その2

  address_add_2_kana: Joi.string()
    .regex(regex.pattern.alphanumericAndKatakana, {
      name: 'alphanumericAndKatakana',
    })
    .max(255)
    .allow(null, ''), // 現場の住所その2

  office_postal_code: Joi.string()
    .regex(regex.pattern.zip)
    .required(), // 事業所の郵便番号
  office_address_prefecture: Joi.string().required(), // 事務所の都道府県
  office_address_city: Joi.string()
    .regex(regex.pattern.otherThanZenkakuMarkAndNumeric, {
      name: 'otherThanZenkakuMarkAndNumeric',
    })
    .max(255)
    .required(), // 事業所の市区町村

  office_address_city_kana: Joi.string()
    .regex(regex.pattern.alphanumericAndKatakana, {
      name: 'alphanumericAndKatakana',
    })
    .max(255)
    .required(), // 事業所の市区町村かな

  office_address_add_1: Joi.string()
    .regex(regex.pattern.otherThanZenkakuMarkAndNumeric, {
      name: 'otherThanZenkakuMarkAndNumeric',
    })
    .max(255)
    .required(), // 事業所の住所その１

  office_address_add_1_kana: Joi.string()
    .regex(regex.pattern.alphanumericAndKatakana, {
      name: 'alphanumericAndKatakana',
    })
    .max(255)
    .required(), // 事業所の住所その１

  office_address_add_2: Joi.string()
    .regex(regex.pattern.otherThanZenkakuMarkAndNumeric, {
      name: 'otherThanZenkakuMarkAndNumeric',
    })
    .max(255)
    .allow(null, ''), // 事業所の住所その2

  office_address_add_2_kana: Joi.string()
    .regex(regex.pattern.alphanumericAndKatakana, {
      name: 'alphanumericAndKatakana',
    })
    .max(255)
    .allow(null, ''), // 事業所の住所その2

  latitude: Joi.string(), // 現場の緯度(今後のアップデート次第で自動入力になるかも)
  longitude: Joi.string(), // 現場の経度(今後のアップデート次第で自動入力になるかも)

  charge_name: Joi.string()
    .max(127)
    .required(), // 所長名
  charge_name_kana: Joi.string()
    .regex(regex.pattern.hiragana)
    .max(255)
    .required(), // 所長名(かな)
  manager_id: Joi.string()
    .regex(regex.pattern.integerNoSign, {
      name: 'integerNoSign',
    })
    .length(14)
    .allow(null), // CCUS管理者ID

  department: Joi.string().max(127), // 部署名

  tel: Joi.string()
    .tel()
    .required(), // 現場の代表電話番号
  email: Joi.string().regex(regex.pattern.email), // 現場の代表メールアドレス

  weather_url: Joi.string().regex(regex.pattern.url), // 天気情報のURL

  work_copy_time: Joi.string(), // 予定→実施入力反映時刻
  work_copy_time_night: Joi.string(), // 予定→実施入力反映時刻(夜間)
  work_copy_time_midnight: Joi.string(), // 予定→実施入力反映時刻(深夜)

  work_end_time: Joi.string(), // 作業時間帯バー表示の終了時刻
  work_end_time_night: Joi.string(), // 作業時間帯バー表示の終了時刻(夜間)
  work_end_time_midnight: Joi.string(), // 作業時間帯バー表示の終了時刻(深夜)

  work_start_time: Joi.string(), // 作業時間帯バー表示の開始時刻
  work_start_time_night: Joi.string(), // 作業時間帯バー表示の開始時刻(夜間)
  work_start_time_midnight: Joi.string(), // 作業時間帯バー表示の開始時刻(深夜)

  earlytime_end: Joi.string(), // 早出終了時刻
  earlytime_end_night: Joi.string(), // 早出終了時刻(夜間)
  earlytime_end_midnight: Joi.string(), // 早出終了時刻(深夜)

  overtime_start: Joi.string(), // 残業開始時刻
  overtime_start_night: Joi.string(), // 残業開始時刻(夜間)
  overtime_start_midnight: Joi.string(), // 残業開始時刻(深夜)

  work_hour_zone_day_flag: Joi.number(), // 現場の作業時間帯(昼間)利用フラグ
  work_hour_zone_night_flag: Joi.number(), // 現場の作業時間帯(夜間)利用フラグ
  work_hour_zone_midnight_flag: Joi.number(), // 現場の作業時間帯(深夜)利用フラグ

  start_day: Joi.string()
    .regex(/^([1-9]|[12][0-9]|3[01])$/)
    .required(), // 月次開始日
  approval_days: Joi.string()
    .regex(/^(0|[1-9][0-9]{0,2})$/)
    .required(), // 自動ロックまでの日数
  use_partner_company_approval_flag: Joi.number(), // 協力会社承認機能
  adjust_work_print_flag: Joi.number(), // 職長の作業予定の印刷方法
  crane_use_print_flag: Joi.number(), // 揚重作業計画・作業指示書の印刷向き 0:A4横型、1:A4縦型
  work_and_diary_print_flag: Joi.number(), // 作業間連絡及び調整実施記録（安全衛生日誌有）の印刷方法 0:調整日の作業内容を出力 1:予定日の作業内容のみ出力
  oxygen_lack_print_flag: Joi.number(), // 酸欠帳票印刷フラグ
  groundbreaking_date: Joi.string()
    .regex(regex.pattern['y/m/d'])
    .required(), //着工日
  complete_schedule_date: Joi.string()
    .regex(regex.pattern['y/m/d'])
    .required(), //竣工日
  total_workers_num: Joi.string(), // 作業員数累計
  print_total_manual_operation: Joi.number(), // 帳票の累計欄空白設定フラグ

  management_no: Joi.string()
    .alphanum()
    .max(16)
    .required(), // 工事管理番号 16桁より小さい
  approval_flag: Joi.number(), // 承認フラグ、デフォルト0。0：未承認状態、1:承認状態
  create_manager_user_id: Joi.number(), // 作成した監督のユーザーID

  block_flag: Joi.number(), // 停止フラグ。0:稼動中、1：停止中
  partner_input_result_flag: Joi.number(), // 作業実績未入力チェックフラグ(協力会社)
  use_gk_flg: Joi.number(), // 入退場利用フラグ。0：未利用、１：利用
  use_roan_flg: Joi.number(), // 労務安全利用フラグ。0：未利用、１：利用

  jvs: Joi.array().items(
    Joi.object().keys({
      company_id: Joi.number().required(),
      company_branch_id: Joi.number().required(),
    })
  ),

  work_hour: Joi.array()
    .required()
    .items(
      //サブJVの登録時に必要
      Joi.object().keys({
        start_date: Joi.string()
          .required()
          .regex(regex.pattern['y/m/d']), // 適用開始日
        worker_work_time: Joi.string().required(), //作業員の稼働時間
        manager_work_time: Joi.string().required(), //社員(外注含)の稼働時間
        end_date: Joi.string().regex(regex.pattern['y/m/d']), //適用終了日
      })
    ),

  company_branch_id: Joi.number().required(), // 支店ID

  buildee_user_id: Joi.number().required(), // BuildeeユーザーID 利用者一覧からプルダウン選択。本社管理者、支店管理者のときに使用

  // Buildee利用終了日
  usage_end_date: Joi.string()
    .regex(regex.pattern['y/m/d'])
    .required(),
};
export const FieldScheme = Joi.object().keys(fieldSchemeKeys);

/**
 * @description CCUS現場登録用のバリデート
 */
export const FieldCcusScheme = Joi.object().keys(
  _.assign(fieldSchemeKeys, {
    postal_code: Joi.string()
      .regex(regex.pattern.zip)
      .required(), // 現場の郵便番号
    ordering_type_id: Joi.number().required(), //発注区分
    construction_type_id: Joi.number().required(), //工事種類
    manager_id: Joi.string()
      .regex(regex.pattern.integerNoSign, {
        name: 'integerNoSign',
      })
      .length(14)
      .required(), // CCUS管理者ID
  })
);

export const FieldCopyScheme = Joi.object().keys(
  _.pick(fieldSchemeKeys, COPY_FIELD_ADDRESS_TARGET)
);

/**
 * @description 利用者登録・編集画面で使う バリデーション object
 */
export const UserScheme = Joi.object().keys({
  user_image: Joi.any(), // ユーザーアイコン画像
  user_role_code: Joi.number().required(), // ユーザー種別
  name_sei: Joi.string()
    .max(64)
    .required(), //姓
  name_mei: Joi.string()
    .max(64)
    .required(), //名
  kana_sei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), //せい
  kana_mei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), //めい
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow('--', null, ''), //携帯電話番号
  email: Joi.string()
    .max(255)
    .regex(regex.pattern.email)
    .required(), //Eメールアドレス
});

/**
 * @description 利用者登録画面 (協力会社) で使う バリデーション object (管理者用)
 */
export const UserPartnerManagerScheme = Joi.object().keys({
  user_role_code: Joi.number().required(), // ユーザー種別
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), //姓
  name_mei: Joi.string()
    .max(64)
    .required(), //名
  kana_sei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), //せい
  kana_mei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), //めい
  company_id: Joi.number().required(), // 元請会社ID
  email: Joi.string()
    .regex(regex.pattern.email)
    .required(), //Eメールアドレス
  tel: Joi.string().regex(regex.pattern.tel), //携帯電話番号
  fax: Joi.string().regex(regex.pattern.tel),
  company_branch_id: Joi.number().required(), //支店ID
  ccus_flag: Joi.string(),
  ccus_flag_admin: Joi.any().when('ccus_flag', {
    is: ['0', '1'],
    then: Joi.required(),
  }),
});

/**
 * @description 利用者登録画面 (協力会社) で使う バリデーション object (管理者用)
 *              ※company_id(元請会社ID)company_branch_id(支店ID)が無い
 */
export const UserPartnerManagerForEditScheme = Joi.object().keys({
  user_role_code: Joi.number().required(), // ユーザー種別
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), // 姓
  name_mei: Joi.string()
    .max(64)
    .required(), // 名
  kana_sei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), // せい
  kana_mei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), // めい
  email: Joi.string()
    .regex(regex.pattern.email)
    .required(), // Eメールアドレス
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow('--', null, ''), // 携帯電話番号
  fax: Joi.string()
    .regex(regex.pattern.tel)
    .allow('--', null, ''), // FAX
});

/**
 * @description 利用者登録画面 (協力会社) で使う バリデーション object (職長用)
 */
export const UserPartnerScheme = Joi.object().keys({
  user_role_code: Joi.number().required(), // ユーザー種別
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), //姓
  name_mei: Joi.string()
    .max(64)
    .required(), //名
  kana_sei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), //せい
  kana_mei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), //めい
  company_id: Joi.number().required(), // 元請会社ID
  login_id: Joi.string()
    .max(60)
    .alphanum()
    .regex(regex.pattern.email, { invert: true })
    .required(), //ユーザーID
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow('--', null, ''), //携帯電話番号
  email: Joi.string().regex(regex.pattern.email), //Eメールアドレス
  company_branch_id: Joi.number().required(), //支店ID
});

/**
 * @description 利用者編集画面 (協力会社) で使う バリデーション object
 *              ※company_id(元請会社ID)company_branch_id(支店ID)が無い
 */
export const UserPartnerForEditScheme = Joi.object().keys({
  user_role_code: Joi.number().required(), // ユーザー種別
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), // 姓
  name_mei: Joi.string()
    .max(64)
    .required(), // 名
  kana_sei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), // せい
  kana_mei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), // めい
  login_id: Joi.string()
    .max(60)
    .alphanum()
    .regex(regex.pattern.email, { invert: true })
    .required(), // ユーザーID
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow('--', null, ''), // 携帯電話番号
  email: Joi.string()
    .regex(regex.pattern.email)
    .allow(null, ''), // Eメールアドレス
});

export const UserPartnerIntegratedForEditScheme = Joi.object().keys({
  user_role_code: Joi.number().required(), // ユーザー種別
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), // 姓
  name_mei: Joi.string()
    .max(64)
    .required(), // 名
  kana_sei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), // せい
  kana_mei: Joi.string()
    .max(128)
    .regex(regex.pattern.hiragana)
    .required(), // めい
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow('--', null, ''), // 携帯電話番号
  email: Joi.string()
    .regex(regex.pattern.email)
    .required(), //Eメールアドレス
});

/**
 * @description アカウント編集画面で使うバリデーションオブジェクト
 */
export const UserProfileScheme = Joi.object().keys({
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), //姓
  name_mei: Joi.string()
    .max(64)
    .required(), //名
  kana_sei: Joi.string()
    .max(128)
    .required()
    .regex(regex.pattern.hiragana), //せい
  kana_mei: Joi.string()
    .max(128)
    .required()
    .regex(regex.pattern.hiragana), //めい
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow(null, '', '--'), // 電話番号
  fax: Joi.string()
    .regex(regex.pattern.fax)
    .allow(null, '--'), //FAX番号
  email: Joi.string()
    .max(255)
    .required()
    .regex(regex.pattern.email), //Eメールアドレス
  company_id: Joi.number(), // 元請ID
  current_password: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // 現在のパスワード
  password: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // パスワード
  password_confirmation: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // パスワード(確認)
  login_id: Joi.string()
    .regex(regex.pattern.login)
    .max(60),
});

/**
 * @description UserProfileScheme・パスワード系必須版
 */
const PASSWORDS_SCHEME = {
  current_password: Joi.string()
    .max(32)
    .required(), // 現在のパスワード
  password: Joi.string()
    .max(32)
    .required()
    .regex(regex.pattern.pwdPWD32), // パスワード
  password_confirmation: Joi.string()
    .max(32)
    .required()
    .regex(regex.pattern.pwdPWD32), // パスワード(確認)
};

/**
 * @description UserProfileSchemeにパスワード系を必須にしたもの
 */
export const UserProfileSchemeWithPassword = UserProfileScheme.append(
  PASSWORDS_SCHEME
);

/**
 * @description アカウント編集画面(職長: foreman)で使うバリデーションオブジェクト
 */
export const UserProfileSchemeForeman = Joi.object().keys({
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), //姓
  name_mei: Joi.string()
    .max(64)
    .required(), //名
  kana_sei: Joi.string()
    .max(128)
    .required()
    .regex(regex.pattern.hiragana), //せい
  kana_mei: Joi.string()
    .max(128)
    .required()
    .regex(regex.pattern.hiragana), //めい
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow(null, '', '--'), // 電話番号
  email: Joi.string()
    .max(255)
    .regex(regex.pattern.email), //Eメールアドレス
  company_id: Joi.number(), // 元請ID
  current_password: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // 現在のパスワード
  password: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // パスワード
  password_confirmation: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // パスワード
  // ログインID
  login_id: Joi.string()
    .required()
    .regex(regex.pattern.login)
    .max(60),
});

export const UserProfileSchemeForemanIntegrated = Joi.object().keys({
  user_image: Joi.any(), // ユーザーアイコン画像
  name_sei: Joi.string()
    .max(64)
    .required(), //姓
  name_mei: Joi.string()
    .max(64)
    .required(), //名
  kana_sei: Joi.string()
    .max(128)
    .required()
    .regex(regex.pattern.hiragana), //せい
  kana_mei: Joi.string()
    .max(128)
    .required()
    .regex(regex.pattern.hiragana), //めい
  tel: Joi.string()
    .regex(regex.pattern.tel)
    .allow(null, '', '--'), // 電話番号
  email: Joi.string()
    .max(255)
    .required()
    .regex(regex.pattern.email), //Eメールアドレス
  company_id: Joi.number(), // 元請ID
  current_password: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // 現在のパスワード
  password: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // パスワード
  password_confirmation: Joi.string()
    .max(32)
    .regex(regex.pattern.pwdPWD32), // パスワード
  // ログインID
  login_id: Joi.string()
    .regex(regex.pattern.login)
    .max(60),
});

/**
 * @description UserProfileSchemeForemanにパスワード系を必須にしたもの
 */
export const UserProfileSchemeForemanWithPassword = UserProfileSchemeForeman.append(
  PASSWORDS_SCHEME
);

/**
 * @description 必須項目・普通のロール
 */
export const UserProfileRequired = {
  name_sei: false,
  name_mei: false,
  kana_sei: false,
  kana_mei: false,
  email: false,
};

/**
 * @description 必須項目・職長のロール
 */
export const UserProfileRequiredForeman = {
  name_sei: false,
  name_mei: false,
  kana_sei: false,
  kana_mei: false,
  login_id: false,
};

export const UserProfileRequiredForemanIntegrated = {
  name_sei: false,
  name_mei: false,
  kana_sei: false,
  kana_mei: false,
  email: false,
};

/**
 * @description 必須項目・パスワードの切り出し
 */
export const PasswordRequired = {
  current_password: false,
  password: false,
  password_confirmation: false,
};

/**
 * @description 企業管理者申請画面で使うバリデーションオブジェクト
 */
export const BuildeeManagerRequestScheme = Joi.object().keys({
  user_id: Joi.number().required(), //ユーザー
  file_name: Joi.string().required(), //ファイル
});



// WEBPACK FOOTER //
// ./src/utilities/JoiSchemes.js